import { useState, FormEvent } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Loader2 } from "lucide-react";
import { REACH_BUCKETS, CHANNEL_OPTIONS } from "@/lib/affiliate/programTerms";

interface Props {
  onSuccess: () => void;
}

const ERROR_MESSAGES: Record<string, string> = {
  invalid_name: "Please enter your full name.",
  invalid_email: "Please enter a valid email address.",
  invalid_audience: "Tell us a bit more about your audience (at least 10 characters).",
  invalid_reach: "Please select your monthly reach.",
  rate_limited: "You've already submitted an application in the last 24 hours.",
  server_error: "Something went wrong on our end. Please try again in a moment.",
  email_failed: "Application saved, but we couldn't send a confirmation email.",
};

export default function AffiliateApplyForm({ onSuccess }: Props) {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [channels, setChannels] = useState<string[]>([]);

  function toggleChannel(c: string) {
    setChannels((prev) => (prev.includes(c) ? prev.filter((x) => x !== c) : [...prev, c]));
  }

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);

    const fd = new FormData(e.currentTarget);
    const payload = {
      full_name: String(fd.get("full_name") || ""),
      email: String(fd.get("email") || ""),
      company: String(fd.get("company") || ""),
      url: String(fd.get("url") || ""),
      audience: String(fd.get("audience") || ""),
      monthly_reach: String(fd.get("monthly_reach") || ""),
      channels,
      // honeypot
      website_url: String(fd.get("website_url") || ""),
    };

    try {
      const { data, error: invokeError } = await supabase.functions.invoke(
        "submit-affiliate-application",
        { body: payload },
      );

      if (invokeError) {
        setError(ERROR_MESSAGES.server_error);
      } else if (!data?.success) {
        setError(ERROR_MESSAGES[data?.error] || ERROR_MESSAGES.server_error);
      } else {
        onSuccess();
      }
    } catch {
      setError(ERROR_MESSAGES.server_error);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      {/* Honeypot — hidden from humans, visible to bots */}
      <input
        type="text"
        name="website_url"
        tabIndex={-1}
        autoComplete="off"
        className="hidden"
        aria-hidden="true"
      />

      <Field label="Full name" name="full_name" required maxLength={120} autoComplete="name" />
      <Field label="Email" name="email" type="email" required maxLength={255} autoComplete="email" />
      <Field label="Company (optional)" name="company" maxLength={120} />
      <Field
        label="Website / channel URL (optional)"
        name="url"
        type="url"
        maxLength={500}
        placeholder="https://"
      />

      <div>
        <label className="block text-sm font-medium mb-2 text-white">Monthly reach</label>
        <select
          name="monthly_reach"
          required
          defaultValue=""
          className="w-full px-3 py-2 rounded-md bg-[#0a0d12] border border-[#1e2530] text-white focus:outline-none focus:border-[#1ab7aa]"
        >
          <option value="" disabled>Select reach…</option>
          {REACH_BUCKETS.map((r) => (
            <option key={r.value} value={r.value}>{r.label}</option>
          ))}
        </select>
      </div>

      <div>
        <label className="block text-sm font-medium mb-2 text-white">Where do you reach your audience?</label>
        <div className="grid grid-cols-2 gap-2">
          {CHANNEL_OPTIONS.map((c) => {
            const active = channels.includes(c);
            return (
              <button
                key={c}
                type="button"
                onClick={() => toggleChannel(c)}
                className={`px-3 py-2 rounded-md text-sm text-left border transition-colors ${
                  active
                    ? "bg-[#1ab7aa]/10 border-[#1ab7aa] text-[#1ab7aa]"
                    : "bg-[#0a0d12] border-[#1e2530] text-[#d1d5db] hover:border-[#374151]"
                }`}
              >
                {c}
              </button>
            );
          })}
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium mb-2 text-white">
          Tell us about your audience
        </label>
        <textarea
          name="audience"
          required
          minLength={10}
          maxLength={2000}
          rows={5}
          placeholder="Who follows you? What do they care about? How would you promote Lanerunner?"
          className="w-full px-3 py-2 rounded-md bg-[#0a0d12] border border-[#1e2530] text-white focus:outline-none focus:border-[#1ab7aa] resize-y"
        />
      </div>

      {error && (
        <div className="rounded-md border border-red-500/40 bg-red-500/10 px-3 py-2 text-sm text-red-300">
          {error}
        </div>
      )}

      <button
        type="submit"
        disabled={submitting}
        className="w-full inline-flex items-center justify-center gap-2 px-6 py-3 rounded-lg bg-[#1ab7aa] text-black font-semibold hover:bg-[#15a299] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
        {submitting ? "Submitting…" : "Submit application"}
      </button>
      <p className="text-xs text-[#6b7280] text-center">
        By applying, you agree to our affiliate terms and program rules.
      </p>
    </form>
  );
}

function Field({
  label,
  name,
  type = "text",
  required = false,
  maxLength,
  placeholder,
  autoComplete,
}: {
  label: string;
  name: string;
  type?: string;
  required?: boolean;
  maxLength?: number;
  placeholder?: string;
  autoComplete?: string;
}) {
  return (
    <div>
      <label className="block text-sm font-medium mb-2 text-white">{label}</label>
      <input
        type={type}
        name={name}
        required={required}
        maxLength={maxLength}
        placeholder={placeholder}
        autoComplete={autoComplete}
        className="w-full px-3 py-2 rounded-md bg-[#0a0d12] border border-[#1e2530] text-white focus:outline-none focus:border-[#1ab7aa]"
      />
    </div>
  );
}
